import { useEffect, useState } from "react";
import { Rnd } from "react-rnd";
import { useNavigate, useParams } from "react-router-dom";
import client from "../api/client";
import { invoicePlaceholders } from "../data/invoicePlaceholders";

const styles = {
  page: {
    height: "calc(100vh - 65px)",
    overflow: "hidden",
    background: "#eef2f6",
    fontSize: 11,
  },

  layout: {
    display: "grid",
    gridTemplateColumns: "190px minmax(0,1fr) 230px",
    height: "100%",
  },

  left: {
    background: "#fff",
    borderRight: "1px solid #dee2e6",
    overflowY: "auto",
    padding: 10,
  },

  right: {
    background: "#fff",
    borderLeft: "1px solid #dee2e6",
    overflowY: "auto",
    padding: 10,
  },

  title: {
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 8,
    color: "#0d6efd",
  },

  input: {
    fontSize: 11,
    borderRadius: 7,
    height: 30,
  },

  textarea: {
    fontSize: 11,
    borderRadius: 7,
  },

  btn: {
    width: "100%",
    fontSize: 11,
    borderRadius: 7,
    marginBottom: 6,
    height: 30,
    fontWeight: 600,
  },

  canvasArea: {
    overflow: "auto",
    padding: 12,
  },

  pageBox: {
    width: 794,
    height: 1123,
    background: "#fff",
    border: "1px solid #ced4da",
    margin: "0 auto",
    position: "relative",
    boxShadow: "0 4px 16px rgba(0,0,0,.1)",
  },

  propertyLabel: {
    fontSize: 10,
    fontWeight: 700,
    marginBottom: 3,
    color: "#495057",
  },

  placeholderBox: {
    maxHeight: 280,
    overflowY: "auto",
  },

  placeholderItem: {
    fontSize: 10,
    padding: "5px 6px",
    borderRadius: 6,
    background: "#f8f9fa",
    border: "1px solid #dee2e6",
    marginBottom: 4,
    cursor: "pointer",
    wordBreak: "break-word",
  },
};

export default function InvoiceTemplateDesignerPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [templateName, setTemplateName] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const [blocks, setBlocks] = useState([
    {
      id: crypto.randomUUID(),
      type: "text",
      label: "Company Name",
      value: "{{CompanyName}}",
      x: 35,
      y: 25,
      width: 250,
      height: 35,
      fontSize: 16,
      bold: true,
      align: "left",
    },
  ]);

  const selectedBlock = blocks.find((x) => x.id === selectedId);

  useEffect(() => {
    if (id) loadTemplate();
  }, [id]);

  const loadTemplate = async () => {
    const res = await client.get(`/invoice-templates/${id}`);
    setTemplateName(res.data.templateName);

    const parsed = JSON.parse(res.data.templateJson);

    setBlocks(parsed.blocks || []);
  };

  const updateBlock = (blockId, values) => {
    setBlocks((prev) =>
      prev.map((b) => (b.id === blockId ? { ...b, ...values } : b)),
    );
  };

  const addTextBlock = () => {
    setBlocks([
      ...blocks,
      {
        id: crypto.randomUUID(),
        type: "text",
        label: "Text",
        value: "{{CustomerName}}",
        x: 40,
        y: 100,
        width: 220,
        height: 30,
        fontSize: 12,
        bold: false,
        align: "left",
      },
    ]);
  };

  const addBoxBlock = () => {
    setBlocks([
      ...blocks,
      {
        id: crypto.randomUUID(),
        type: "box",
        label: "Box",
        x: 40,
        y: 160,
        width: 280,
        height: 90,
      },
    ]);
  };

  const addTable = () => {
    setBlocks([
      ...blocks,
      {
        id: crypto.randomUUID(),
        type: "table",
        label: "Items Table",
        x: 35,
        y: 300,
        width: 720,
        height: 210,
      },
    ]);
  };

  const deleteSelected = () => {
    if (!selectedId) return;

    setBlocks(blocks.filter((x) => x.id !== selectedId));

    setSelectedId(null);
  };

  const saveTemplate = async () => {
    if (!templateName.trim()) {
      alert("Enter template name");
      return;
    }

    const payload = {
      templateName,
      templateJson: JSON.stringify({
        pageSize: "A4",
        blocks,
      }),
    };

    if (id) {
      await client.put(`/invoice-templates/${id}`, payload);
      alert("Template updated successfully");
    } else {
      await client.post("/invoice-templates", payload);
      alert("Template saved successfully");
    }

    navigate("/invoice-templates");
  };

  return (
    <div style={styles.page}>
      <div style={styles.layout}>
        <div style={styles.left}>
          <div style={styles.title}>
            <i className="bi bi-tools me-2"></i>
            Designer Tools
          </div>

          <input
            className="form-control form-control-sm mb-2"
            style={styles.input}
            placeholder="Template Name"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
          />

          <button
            className="btn btn-primary btn-sm"
            style={styles.btn}
            onClick={addTextBlock}
          >
            Add Text
          </button>

          <button
            className="btn btn-secondary btn-sm"
            style={styles.btn}
            onClick={addBoxBlock}
          >
            Add Box
          </button>

          <button
            className="btn btn-dark btn-sm"
            style={styles.btn}
            onClick={addTable}
          >
            Add Items Table
          </button>

          <button
            className="btn btn-danger btn-sm"
            style={styles.btn}
            onClick={deleteSelected}
          >
            Delete Selected
          </button>

          <button
            className="btn btn-success btn-sm"
            style={styles.btn}
            onClick={saveTemplate}
          >
            Save Template
          </button>

          <hr />

          <div className="fw-bold mb-2" style={{ fontSize: 11 }}>
            Placeholders
          </div>

          <div style={styles.placeholderBox}>
            {invoicePlaceholders.map((x) => (
              <div
                key={x}
                style={styles.placeholderItem}
                onClick={() => {
                  navigator.clipboard.writeText(x);
                  alert(`${x} copied`);
                }}
              >
                {x}
              </div>
            ))}
          </div>
        </div>

        <div style={styles.canvasArea}>
          <div style={styles.pageBox}>
            {blocks.map((block) => (
              <Rnd
                key={block.id}
                size={{
                  width: block.width,
                  height: block.height,
                }}
                position={{
                  x: block.x,
                  y: block.y,
                }}
                bounds="parent"
                onDragStop={(e, d) =>
                  updateBlock(block.id, {
                    x: d.x,
                    y: d.y,
                  })
                }
                onResizeStop={(e, direction, ref, delta, position) =>
                  updateBlock(block.id, {
                    width: ref.offsetWidth,
                    height: ref.offsetHeight,
                    x: position.x,
                    y: position.y,
                  })
                }
                onClick={() => setSelectedId(block.id)}
              >
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    padding: 4,
                    overflow: "hidden",
                    cursor: "move",
                    background: block.type === "box" ? "transparent" : "#fff",
                    border:
                      selectedId === block.id
                        ? "2px solid #0d6efd"
                        : "1px dashed #999",
                    fontSize: block.fontSize || 12,
                    fontWeight: block.bold ? "bold" : "normal",
                    textAlign: block.align || "left",
                    lineHeight: 1.2,
                  }}
                >
                  {block.type === "text" && block.value}

                  {block.type === "box" && (
                    <div style={{ width: "100%", height: "100%" }} />
                  )}

                  {block.type === "table" && (
                    <table className="table table-bordered table-sm mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Item</th>
                          <th>Qty</th>
                          <th>Rate</th>
                          <th>Total</th>
                        </tr>
                      </thead>

                      <tbody>
                        <tr>
                          <td>{"{{ItemName}}"}</td>
                          <td>{"{{Qty}}"}</td>
                          <td>{"{{Rate}}"}</td>
                          <td>{"{{Amount}}"}</td>
                        </tr>
                      </tbody>
                    </table>
                  )}
                </div>
              </Rnd>
            ))}
          </div>
        </div>

        <div style={styles.right}>
          <div style={styles.title}>
            <i className="bi bi-sliders me-2"></i>
            Properties
          </div>

          {!selectedBlock && (
            <p className="small text-muted">Select any block</p>
          )}

          {selectedBlock && (
            <>
              <div style={styles.propertyLabel}>Label</div>

              <input
                className="form-control form-control-sm mb-2"
                style={styles.input}
                value={selectedBlock.label || ""}
                onChange={(e) =>
                  updateBlock(selectedBlock.id, {
                    label: e.target.value,
                  })
                }
              />

              {selectedBlock.type === "text" && (
                <>
                  <div style={styles.propertyLabel}>Value</div>

                  <textarea
                    rows="3"
                    className="form-control form-control-sm mb-2"
                    style={styles.textarea}
                    value={selectedBlock.value || ""}
                    onChange={(e) =>
                      updateBlock(selectedBlock.id, {
                        value: e.target.value,
                      })
                    }
                  />

                  <div style={styles.propertyLabel}>Font Size</div>

                  <input
                    type="number"
                    className="form-control form-control-sm mb-2"
                    style={styles.input}
                    value={selectedBlock.fontSize || 12}
                    onChange={(e) =>
                      updateBlock(selectedBlock.id, {
                        fontSize: Number(e.target.value),
                      })
                    }
                  />

                  <div style={styles.propertyLabel}>Alignment</div>

                  <select
                    className="form-select form-select-sm mb-2"
                    style={styles.input}
                    value={selectedBlock.align || "left"}
                    onChange={(e) =>
                      updateBlock(selectedBlock.id, {
                        align: e.target.value,
                      })
                    }
                  >
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                    <option value="right">Right</option>
                  </select>

                  <div className="form-check mb-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={selectedBlock.bold || false}
                      onChange={(e) =>
                        updateBlock(selectedBlock.id, {
                          bold: e.target.checked,
                        })
                      }
                    />

                    <label
                      className="form-check-label"
                      style={{ fontSize: 11 }}
                    >
                      Bold
                    </label>
                  </div>
                </>
              )}

              <div className="row g-2">
                {["x", "y", "width", "height"].map((field) => (
                  <div className="col-6" key={field}>
                    <div style={styles.propertyLabel}>
                      {field.toUpperCase()}
                    </div>

                    <input
                      type="number"
                      className="form-control form-control-sm"
                      style={styles.input}
                      value={selectedBlock[field]}
                      onChange={(e) =>
                        updateBlock(selectedBlock.id, {
                          [field]: Number(e.target.value),
                        })
                      }
                    />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
